import apiClient from "./api";
import useTransientStore from '../data/transcientStore';
import { clientId, msalInstance } from "../data/msalConfig"; // Adjust the import path as necessary
import { AUTH_ENABLED } from "../data/authConfig";
import { AddUpdateUserRequestType, UserType } from "../types/User";
import { AddUpdateOrganisationRequest, OrganisationType } from "../types/Organisation";
import { ApplicationAddressSystemType, AddUpdateApplicationAddressSystemType, ContractUserApplicationAddressSystemType } from "../types/ApplicationAddressSystem";
import { Role } from "../types/Role";
import { RIMSPolyline } from "../types/Polylines";

const baseUrl = '/api/UserManagement';

// Utility function to get a valid access token for the API
export const getApiAccessToken = async () => {
    if (!AUTH_ENABLED) return '';
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. User may not be logged in.');
    }
    const request = {
        account: accounts[0],
        scopes: [`api://${clientId}/access_as_user`],
    };
    try {
        const response = await msalInstance.acquireTokenSilent(request);
       // console.log(response.accessToken)
        return response.accessToken;
    } catch (error) {
        console.error('Silent token acquisition failed, falling back to popup.', error);
        const response = await msalInstance.acquireTokenPopup(request);
       // console.log(response.accessToken)
        return response.accessToken;
    }
};

//#region User routes
export const getUserDetails = async (userEmail: string) => {
    // const token = await getApiAccessToken();
    // const response = await apiClient.get(`${baseUrl}/checkUser`, {
    //     headers: {
    //         Authorization: `Bearer ${token}`,
    //         body: {email: userEmail}
    //     }
    // });
    // return response.status === 200;

    return userEmail.length !== 0; // Replace this code with the above when there is enough time to troubleshoot and test.
}

export const getAllUsers = async () => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getAllUsers`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as UserType[];
}

export const getUserIdByEmail = async (email: string) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(
        `${baseUrl}/getUserIdByEmail?email=${encodeURIComponent(email)}`,
        {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }
    );
    // The backend returns { userId: number }
    const data = response.data as { userId: number };
    return data.userId;
}

export const getUserById = async (id: number) => {
    if (typeof id !== 'number' || isNaN(id) || id <= 0) {
        throw new Error('Invalid user id provided to getUserById');
    }
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getUserById/${id}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as UserType;
}

export const getUserByUsername = async (username: string) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/searchByUsername?username=${username}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        }
    });
    return response.data as UserType;
}

export const addUpdateUser = async (data: AddUpdateUserRequestType) => {
    const token = await getApiAccessToken();
    if (data.id === null) {
        const response = await apiClient.post(`${baseUrl}/addUser`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });
        return response.status === 200;
    } else {
        const response = await apiClient.put(`${baseUrl}/updateUser`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });
        return response.status === 200;
    }

}

//#region Organisation Routes
export const getAllOrganisations = async () => {
    const token = await getApiAccessToken();
    const apiResponse = await apiClient.get(`${baseUrl}/getAllOrganisations`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return apiResponse.data as OrganisationType[];
}

export const addUpdateOrganisation = async (data: AddUpdateOrganisationRequest) => {
    const token = await getApiAccessToken();
    if (data.id === null) {
        const apiResponse = await apiClient.post(`${baseUrl}/addOrganisation`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });
        return apiResponse.status === 200;
    } else {
        const apiResponse = await apiClient.put(`${baseUrl}/updateOrganisation`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });
        return apiResponse.status === 200;
    }
}

//#region Address Routes
export const getAllAddressSystems = async () => {
    const token = await getApiAccessToken();
    const apiResponse = await apiClient.get(`${baseUrl}/getAllAddress`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return apiResponse.data as ApplicationAddressSystemType[]
}

export const getAddressSystemById = async (id: number) => {
    const token = await getApiAccessToken();
    const apiResponse = await apiClient.get(`${baseUrl}/getAddressById/${id}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return apiResponse.data as ApplicationAddressSystemType
}

export const addUpdateAddressSystem = async (data: AddUpdateApplicationAddressSystemType) => {
    const token = await getApiAccessToken();
    if (data.id === null) {
        const apiResponse = await apiClient.post(`${baseUrl}/addAddress`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });
        return apiResponse.status === 200;
    } else {
        const apiResponse = await apiClient.put(`${baseUrl}/updateAddress`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            }
        );
        return apiResponse.status === 200;
    }
}

//#region Role Routes
export const getAllRoles = async () => {
    const token = await getApiAccessToken();
    const apiResponse = await apiClient.get(`${baseUrl}/getAllRoles`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return apiResponse.data as Role[];
}

export const getContractUsersForAddressById = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const apiResponse = await apiClient.get(`${baseUrl}/getContractUsersForAddressById/${addressSystemId}`,
        { 
            headers: { Authorization: `Bearer ${token}` 
        } 
    });
    return apiResponse.data as ContractUserApplicationAddressSystemType[];
}